import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import {
  ArrowRight, ShieldCheck, MapPin, Building2, Hammer, Users, Sparkles,
  Network, Heart, GraduationCap, Truck, Wrench, Briefcase, Home as HomeIcon,
  TrendingUp, Handshake, Factory, BadgeDollarSign, Quote,
} from "lucide-react";
import api from "../lib/api";
import { log } from "../lib/logger";
import { Section, Eyebrow, H2, Lead, GlassCard, Reveal, StatBar } from "../components/site/Primitives";
import { SUBSIDIARIES } from "../data/subsidiaries";
import { PublicMap } from "../components/site/Maps";
import StatusBadge from "../components/site/StatusBadge";

const HERO_SLIDES = [
  {
    img: "https://static.prod-images.emergentagent.com/jobs/47a61e19-5c55-4d88-8e31-7b747a09cb17/images/33ecb01f35acf35b99741b6d756b22acc6c0f6327a0f0d1a619ca57038557358.png",
    eyebrow: "Phase 1 · Riverfront Corridor",
    title: "Building the Future.",
    sub: "Veteran-owned. Mission-driven. Engineering the next generation of American communities.",
  },
  {
    img: "https://images.unsplash.com/photo-1620058689342-80271a734f23?w=2200&q=80",
    eyebrow: "Veteran Housing Initiative",
    title: "A promise to those who served.",
    sub: "Transitional, recovery, affordable, and workforce housing — paired with the services veterans actually need.",
  },
  {
    img: "https://images.unsplash.com/photo-1486325212027-8081e485255e?w=2200&q=80",
    eyebrow: "Mixed-Use Development",
    title: "Where people live, work, and grow.",
    sub: "Master-planned riverfront communities anchored by retail, residences, and public-private infrastructure.",
  },
  {
    img: "https://images.unsplash.com/photo-1581094288338-2314dddb7ece?w=2200&q=80",
    eyebrow: "220 Construction",
    title: "Built with discipline.",
    sub: "Self-perform crews. Diverse trade teams. Military-grade planning from site prep to interior finish.",
  },
  {
    img: "https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?w=2200&q=80",
    eyebrow: "220 Event Center",
    title: "A destination venue for the community.",
    sub: "Weddings, corporate events, conferences, banquets, fundraisers, concerts, and veteran celebrations — under one luxurious roof.",
  },
];

const ECOSYSTEM_GROUPS = [
  {
    id: "build",
    title: "Build",
    desc: "We physically build the community — from land to keys.",
    icon: <Hammer />,
    subs: ["construction", "realty", "property-management", "land-development"],
  },
  {
    id: "serve",
    title: "Serve",
    desc: "We provide housing, services, and stability to those who need them.",
    icon: <Heart />,
    subs: ["veteran-housing", "support-services"],
  },
  {
    id: "grow",
    title: "Grow",
    desc: "We train and place residents into careers inside the ecosystem.",
    icon: <GraduationCap />,
    subs: ["education-certification", "tech"],
  },
  {
    id: "elevate",
    title: "Elevate",
    desc: "We add lifestyle, mobility, and culture to the platform.",
    icon: <Sparkles />,
    subs: ["club-220", "vip-transportation"],
  },
];

const VETERAN_FACTS = [
  {
    icon: <HomeIcon />,
    big: "33K+",
    label: "U.S. Veterans Experiencing Homelessness",
    body: "Tens of thousands of veterans still face homelessness on any given night. Stable housing is the first step home.",
  },
  {
    icon: <Briefcase />,
    big: "200K",
    label: "Service Members Transition Annually",
    body: "Workforce reintegration requires more than a job board — it requires training, mentorship, and a community.",
  },
  {
    icon: <Building2 />,
    big: "7.4M",
    label: "U.S. Households Cost-Burdened by Rent",
    body: "Affordable housing isn't a niche — it's national infrastructure. 220 builds at every income level.",
  },
  {
    icon: <ShieldCheck />,
    big: "1st",
    label: "Mission Principle",
    body: "Mission-oriented leadership: built by veterans, for the communities veterans came home to.",
  },
];

const PHASES = [
  { phase: "Phase 1", years: "2026 – 2027", focus: "Land acquisition · Rezoning · Remediation · Workforce townhomes", color: "border-gold" },
  { phase: "Phase 2", years: "2027 – 2029", focus: "Mixed-use riverfront · 220 Event Center flagship · Veteran Housing Bldg A", color: "border-white/30" },
  { phase: "Phase 3", years: "2029 – 2031", focus: "Education campus · Industrial park · Veteran Housing expansion", color: "border-white/30" },
  { phase: "Phase 4+", years: "2031+", focus: "Regional expansion · Public-private joint ventures · Asset stabilization", color: "border-white/30" },
];

const PORTALS_TEASER = [
  { icon: <HomeIcon />, label: "Resident Portal", to: "/portal/resident", body: "Lease, case manager, appointments, maintenance." },
  { icon: <Briefcase />, label: "Investor Dashboard", to: "/portal/investor", body: "Phase progress, capital, milestones, documents." },
  { icon: <Building2 />, label: "Property Management", to: "/portal/tenant", body: "Rent payments, documents, maintenance tickets." },
  { icon: <Wrench />, label: "Contractor Portal", to: "/portal/contractor", body: "Live bids, active projects, permit tracking." },
  { icon: <Users />, label: "Admin Console", to: "/portal/admin", body: "All submissions, KPIs, operations overview." },
];

export default function Home() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/stats").then((r) => setStats(r.data)).catch((e) => log.warn("stats fetch failed", e?.message)); }, []);

  return (
    <div className="relative">
      <RotatingHero />

      {/* Marquee impact ticker */}
      <div className="border-y border-white/10 bg-[#08080A] overflow-hidden" data-testid="impact-ticker">
        <div className="flex marquee-track whitespace-nowrap py-5 text-xs uppercase tracking-[0.35em] text-white/40">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center gap-12 px-12">
              <span>Building the Future</span><span className="text-gold">·</span>
              <span>Veteran-Owned</span><span className="text-gold">·</span>
              <span>Columbus, GA</span><span className="text-gold">·</span>
              <span>Phenix City, AL</span><span className="text-gold">·</span>
              <span>10 Planned Divisions</span><span className="text-gold">·</span>
              <span>Phase 1 · In Planning</span><span className="text-gold">·</span>
              <span>Mission-Driven</span><span className="text-gold">·</span>
            </div>
          ))}
        </div>
      </div>

      {/* MISSION STATEMENT */}
      <Section id="mission-statement" className="relative">
        <Reveal>
          <Eyebrow>Mission Statement</Eyebrow>
          <div className="mt-6 max-w-5xl">
            <Quote className="text-gold mb-6" size={40} />
            <p className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.15] text-white">
              220 Inc. exists to <span className="text-gold">rebuild communities</span>, create opportunity,
              support veterans, and develop <span className="text-gold">self-sustaining ecosystems</span> through
              housing, infrastructure, education, and economic growth.
            </p>
          </div>
        </Reveal>
      </Section>

      {/* COUNTERS */}
      <Section id="impact" className="border-t border-white/5">
        <Reveal>
          <Eyebrow>Projected Impact · Phase 1 → 5 · Forward-Looking</Eyebrow>
          <H2 className="max-w-3xl">Numbers we&apos;re building toward — long-term targets, not current operations.</H2>
        </Reveal>
        <div className="mt-16">
          <StatBar
            items={[
              { value: stats?.housing_units_projected ?? 1850, label: "Housing Units (Projected · Phase 1–5)", testId: "stat-units" },
              { value: stats?.jobs_created ?? 4200, label: "Jobs (Projected · 10-yr)", testId: "stat-jobs" },
              { value: stats?.acres_under_development ?? 612, label: "Acres in Master Plan (Targeted)", testId: "stat-acres" },
              { value: stats?.veterans_assisted ?? 380, label: "Veterans to be Assisted (Targeted)", testId: "stat-veterans" },
              { value: stats?.subsidiaries ?? 10, label: "Planned Divisions", testId: "stat-subs" },
            ]}
          />
        </div>
      </Section>

      {/* WHY VETERANS MATTER */}
      <Section id="why-veterans" className="border-t border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-40">
          <div className="absolute top-1/4 -left-32 w-[420px] h-[420px] bg-gold/10 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-0 w-[520px] h-[520px] bg-white/5 rounded-full blur-[140px]" />
        </div>
        <Reveal>
          <Eyebrow>Why Veterans Matter</Eyebrow>
          <H2 className="max-w-4xl">A national problem. A local commitment.</H2>
          <Lead className="mt-6">
            America's veterans face a stack of compounding challenges — homelessness, workforce
            transition, affordable housing scarcity, and disconnection from community. 220 Inc. was
            founded to attack all four with one integrated platform.
          </Lead>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          {VETERAN_FACTS.map((v, i) => (
            <Reveal key={v.label} delay={i * 0.06}>
              <GlassCard className="p-6 h-full">
                <div className="text-gold mb-5">{v.icon}</div>
                <div className="font-display text-5xl text-white mb-2">{v.big}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/50 mb-4">{v.label}</div>
                <p className="text-sm text-white/65 leading-relaxed">{v.body}</p>
              </GlassCard>
            </Reveal>
          ))}
        </div>
        <div className="mt-12">
          <Link to="/veteran-housing" className="btn-primary" data-testid="why-veterans-cta">
            Explore the Veteran Housing Initiative <ArrowRight size={16} />
          </Link>
        </div>
      </Section>

      {/* ECOSYSTEM */}
      <EcosystemSection />

      {/* SUBSIDIARIES (larger cards w/ glow) */}
      <Section id="subs-preview" className="border-t border-white/5">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
          <div>
            <Eyebrow>Ten Planned Divisions</Eyebrow>
            <H2>One platform. Compounding value.</H2>
            <p className="mt-3 text-sm text-white/55 max-w-2xl">Each division is a planned operating arm of the 220 Inc. platform — currently in planning, formation, or pre-launch.</p>
          </div>
          <Link to="/subsidiaries" className="btn-ghost" data-testid="home-all-subs">
            Explore all subsidiaries <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
          {SUBSIDIARIES.slice(0, 6).map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.05}>
              <div className="relative group" data-testid={`home-sub-${s.slug}`}>
                <div className="absolute -inset-px bg-gradient-to-br from-gold/0 via-gold/0 to-gold/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-lg" />
                <GlassCard className="relative overflow-hidden h-full flex flex-col">
                  <Link to={s.href} className="block">
                    <div className="aspect-[16/10] overflow-hidden relative">
                      <img src={s.image} alt={s.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
                      {s.status && (
                        <div className="absolute top-3 left-3">
                          <StatusBadge kind={s.status} />
                        </div>
                      )}
                      <div className="absolute bottom-4 left-5 right-5">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-1">{s.tag}</div>
                      </div>
                    </div>
                  </Link>
                  <div className="p-7 flex-1 flex flex-col">
                    <div className="font-display text-2xl sm:text-3xl mb-3">{s.name}</div>
                    <p className="text-sm text-white/65 leading-relaxed flex-1">{s.short}</p>
                    <div className="mt-6 flex flex-wrap gap-3">
                      <Link to={s.href} className="btn-primary text-xs !py-2.5 !px-4">Explore <ArrowRight size={12} /></Link>
                      <Link to={`/contact?topic=${s.slug}`} className="btn-ghost text-xs !py-2.5 !px-4">Contact</Link>
                    </div>
                  </div>
                </GlassCard>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* MAP */}
      <Section id="map" className="border-t border-white/5">
        <Reveal>
          <Eyebrow>Master Plan · Project Locations</Eyebrow>
          <H2 className="max-w-3xl">Where the work is planned across the corridor.</H2>
          <Lead className="mt-4">A view of planned and targeted 220 Inc. project zones. Locations shown reflect the master-planning scope; parcel-level details and acquisition status are reserved for accredited investors.</Lead>
        </Reveal>
        <div className="mt-12" data-testid="dev-map">
          <PublicMap height={520} />
        </div>
        <div className="mt-4 flex items-center gap-6 text-xs text-white/50 flex-wrap">
          <span className="flex items-center gap-2"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{background:"#D4AF37"}}/> Veteran Housing / Workforce</span>
          <span className="flex items-center gap-2"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{background:"#C0C0C0"}}/> Mixed-Use</span>
          <span className="flex items-center gap-2"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{background:"#9CA3AF"}}/> Land Development</span>
          <span className="flex items-center gap-2"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{background:"#E6B800"}}/> 220 Event Center</span>
          <span className="flex items-center gap-2"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{background:"#60A5FA"}}/> Education</span>
          <span className="flex items-center gap-2"><MapPin size={12} className="text-gold" /> Columbus, GA · Phenix City, AL corridor</span>
        </div>
      </Section>

      {/* INVESTOR-GRADE SECTIONS */}
      <Section id="phases" className="border-t border-white/5">
        <Reveal>
          <Eyebrow>Development Phases</Eyebrow>
          <H2 className="max-w-3xl">A scalable, multi-phase growth plan.</H2>
        </Reveal>
        <div className="mt-14 relative">
          <div className="hidden md:block absolute left-0 right-0 top-12 h-px bg-white/10" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {PHASES.map((p, i) => (
              <Reveal key={p.phase} delay={i * 0.08}>
                <div className={`relative border-l-2 ${p.color} pl-5 md:pl-0 md:pt-12 md:border-l-0`}>
                  <div className="hidden md:block absolute -top-1 left-0 w-3 h-3 bg-gold" />
                  <div className="text-xs uppercase tracking-[0.2em] text-gold mb-2">{p.years}</div>
                  <div className="font-display text-2xl mb-3">{p.phase}</div>
                  <p className="text-sm text-white/65 leading-relaxed">{p.focus}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      <Section id="strategy" className="border-t border-white/5">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal>
            <div className="lg:col-span-5">
              <Eyebrow>Scalable Growth Strategy</Eyebrow>
              <H2>Three reinforcing engines.</H2>
              <Lead className="mt-6">Each engine compounds the others — assets fund services, services fill assets, education staffs both.</Lead>
            </div>
          </Reveal>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              { icon: <TrendingUp />, t: "Asset Engine", b: "Land, residential, mixed-use, and commercial assets generating cash flow and appreciation." },
              { icon: <Users />, t: "Services Engine", b: "Property management, support services, and case management — recurring revenue + community impact." },
              { icon: <GraduationCap />, t: "Talent Engine", b: "Education & certification feeds skilled workers into construction, services, and tech." },
            ].map((s) => (
              <GlassCard key={s.t} className="p-6">
                <div className="text-gold mb-4">{s.icon}</div>
                <div className="font-display text-xl mb-2">{s.t}</div>
                <p className="text-sm text-white/65 leading-relaxed">{s.b}</p>
              </GlassCard>
            ))}
          </div>
        </div>
      </Section>

      <Section id="partnerships" className="border-t border-white/5">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal>
            <div className="lg:col-span-5">
              <Eyebrow>Public–Private Partnerships</Eyebrow>
              <H2>Built with the public sector — not around it.</H2>
              <Lead className="mt-6">
                Cities, counties, VA, workforce boards, and nonprofits are not stakeholders to manage — they're partners we plan with.
              </Lead>
            </div>
          </Reveal>
          <div className="lg:col-span-7 grid grid-cols-2 gap-px bg-white/10 border border-white/10">
            {[
              "Municipal Redevelopment",
              "County Economic Development",
              "VA Medical Centers",
              "Workforce Investment Boards",
              "Faith-Based Partners",
              "Veteran-Serving Nonprofits",
            ].map((p) => (
              <div key={p} className="bg-[#0c0c0e] p-6">
                <Handshake className="text-gold mb-3" size={20} />
                <div className="text-sm">{p}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section id="economic-impact" className="border-t border-white/5">
        <Reveal>
          <Eyebrow>Projected Economic Impact</Eyebrow>
          <H2 className="max-w-3xl">A platform that pays the community back.</H2>
        </Reveal>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {[
            { icon: <Factory />, big: "$420M+", lbl: "Construction Investment (Projected · 10-yr)" },
            { icon: <Users />, big: "4,200+", lbl: "Jobs (Projected)" },
            { icon: <BadgeDollarSign />, big: "$38M", lbl: "Annual Local Tax Revenue (Projected)" },
            { icon: <HomeIcon />, big: "1,850", lbl: "Housing Units (Planned · Phase 1–5)" },
          ].map((m) => (
            <div key={m.lbl} className="bg-[#0c0c0e] p-8">
              <div className="text-gold mb-4">{m.icon}</div>
              <div className="font-display text-3xl sm:text-4xl text-white">{m.big}</div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/40 mt-3">{m.lbl}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* FUTURE-READY PORTALS */}
      <Section id="portals" className="border-t border-white/5">
        <Reveal>
          <Eyebrow>Future-Ready Platform</Eyebrow>
          <H2 className="max-w-3xl">Every stakeholder gets a portal.</H2>
          <Lead className="mt-4">Residents, investors, tenants, contractors, and operators all log into purpose-built dashboards.</Lead>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {PORTALS_TEASER.map((p) => (
            <Link key={p.label} to={p.to} className="group border border-white/10 p-6 hover:border-gold hover:bg-white/[0.02] transition-all">
              <div className="text-gold mb-4">{p.icon}</div>
              <div className="font-display text-lg mb-2">{p.label}</div>
              <p className="text-xs text-white/55 leading-relaxed">{p.body}</p>
              <div className="mt-4 text-xs text-gold opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">Open <ArrowRight size={12} /></div>
            </Link>
          ))}
        </div>
      </Section>

      {/* INVESTOR CTA */}
      <Section id="investor-cta" className="border-t border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gold/10 rounded-full blur-[180px]" />
        </div>
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Eyebrow>For Investors</Eyebrow>
            <H2>A scalable, mission-aligned platform with measurable upside.</H2>
            <Lead className="mt-6">
              Phase 1 focuses on strategic land acquisition, rezoning, remediation, and infrastructure
              upgrades — laying the foundation for a multi-phase, self-sustaining development engine.
            </Lead>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/investors" className="btn-primary" data-testid="cta-invest-portal">
                Open Investor Portal <ArrowRight size={16} />
              </Link>
              <Link to="/contact?topic=investor" className="btn-ghost" data-testid="cta-invest-consult">
                Request Investor Packet
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <GlassCard className="p-8">
              <div className="text-xs uppercase tracking-[0.2em] text-gold mb-3 flex items-center gap-2">
                <Sparkles size={14} /> Phase 1 Snapshot · Targets
              </div>
              <div className="space-y-5">
                <Row label="Capital Target" value="$28.5M" />
                <Row label="Capital Stage" value="Soft-Circling" />
                <Row label="Development Status" value="In Planning" />
                <Row label="Projected 5-yr ROI" value="18–24%" />
                <Row label="Veteran-Priority Units (Planned)" value="540+" />
              </div>
              <div className="mt-5 pt-4 border-t border-white/10 text-[10px] text-white/45 leading-relaxed">
                Forward-looking targets. Capital not yet closed. No assets acquired or facilities operating at this stage.
              </div>
            </GlassCard>
          </div>
        </div>
      </Section>
    </div>
  );
}

function RotatingHero() {
  const [idx, setIdx] = useState(0);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_SLIDES.length), 6500);
    return () => clearInterval(t);
  }, []);

  const slide = HERO_SLIDES[idx];

  return (
    <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden grain" data-testid="hero">
      {/* slides */}
      <AnimatePresence mode="sync">
        <motion.div
          key={idx}
          className="absolute inset-0"
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ opacity: { duration: 1.4 }, scale: { duration: 8, ease: "linear" } }}
        >
          <motion.img src={slide.img} alt="" className="w-full h-full object-cover" style={{ y }} />
        </motion.div>
      </AnimatePresence>
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/65 to-[#0A0A0A]" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-transparent" />
      {/* spotlight glow */}
      <div className="absolute -top-32 -left-32 w-[600px] h-[600px] bg-gold/15 rounded-full blur-[160px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[700px] h-[400px] bg-white/5 rounded-full blur-[180px] pointer-events-none" />

      <motion.div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32 pt-32 w-full" style={{ opacity }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2 }}>
          <div className="inline-flex items-center gap-2 text-[10px] sm:text-xs uppercase tracking-[0.25em] text-gold mb-6 border border-gold/30 px-3 py-2 bg-black/40">
            <ShieldCheck size={14} /> Veteran-Owned · Columbus, GA · Phenix City, AL
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="text-xs sm:text-sm uppercase tracking-[0.35em] text-gold mb-6">{slide.eyebrow}</div>
            <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl tracking-tighter font-medium leading-[0.95] text-white max-w-5xl">
              {slide.title}
            </h1>
            <p className="mt-6 sm:mt-8 text-base sm:text-xl text-white/80 max-w-2xl leading-relaxed">
              {slide.sub}
            </p>
          </motion.div>
        </AnimatePresence>

        <div className="mt-10 sm:mt-12 flex flex-wrap gap-3 sm:gap-4">
          <Link to="/investors" className="btn-primary" data-testid="hero-invest-cta">
            Invest in Phase 1 <ArrowRight size={16} />
          </Link>
          <Link to="/contact?topic=partner" className="btn-ghost" data-testid="hero-partner-cta">
            Partner With Us
          </Link>
          <Link to="/contact?topic=consult" className="btn-ghost hidden sm:inline-flex" data-testid="hero-consult-cta">
            Schedule a Consultation
          </Link>
        </div>

        {/* progress dots */}
        <div className="mt-12 sm:mt-16 flex items-center gap-3">
          {HERO_SLIDES.map((s, i) => (
            <button
              key={s.eyebrow}
              onClick={() => setIdx(i)}
              className={`h-1 transition-all ${i === idx ? "w-12 bg-gold" : "w-6 bg-white/20 hover:bg-white/40"}`}
              data-testid={`hero-dot-${i}`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </motion.div>

      {/* scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
        className="absolute bottom-8 right-8 hidden md:flex flex-col items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/40"
      >
        <span>Scroll</span>
        <motion.div className="w-px h-12 bg-gradient-to-b from-gold to-transparent" animate={{ scaleY: [1, 0.4, 1] }} transition={{ duration: 2, repeat: Infinity }} />
      </motion.div>
    </section>
  );
}

function EcosystemSection() {
  const [active, setActive] = useState("build");
  const group = ECOSYSTEM_GROUPS.find((g) => g.id === active);
  const subsInGroup = group.subs.map((slug) => SUBSIDIARIES.find((s) => s.slug === slug)).filter(Boolean);

  return (
    <Section id="ecosystem" className="border-t border-white/5 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gold/[0.06] rounded-full blur-[140px]" />
      </div>
      <Reveal>
        <Eyebrow>The 220 Ecosystem</Eyebrow>
        <H2 className="max-w-4xl">How every division connects.</H2>
        <Lead className="mt-6">
          220 Inc. is not a holding company of unrelated assets — it's a closed-loop ecosystem.
          We <span className="text-gold">build</span> the assets, <span className="text-gold">serve</span> the residents,
          <span className="text-gold"> grow</span> the workforce, and <span className="text-gold">elevate</span> the culture.
        </Lead>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10 relative">
        {/* Tabs */}
        <div className="lg:col-span-4 space-y-3">
          {ECOSYSTEM_GROUPS.map((g) => (
            <button
              key={g.id}
              onClick={() => setActive(g.id)}
              className={`w-full text-left border p-5 transition-all ${
                active === g.id
                  ? "border-gold bg-gold/5"
                  : "border-white/10 hover:border-white/30"
              }`}
              data-testid={`eco-tab-${g.id}`}
            >
              <div className="flex items-start gap-4">
                <div className={active === g.id ? "text-gold" : "text-white/50"}>{g.icon}</div>
                <div>
                  <div className="font-display text-xl">{g.title}</div>
                  <div className="text-xs text-white/55 mt-1 leading-relaxed">{g.desc}</div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Hub-and-spoke */}
        <div className="lg:col-span-8 relative min-h-[440px] border border-white/10 p-8 bg-[#0c0c0e] overflow-hidden">
          <svg className="absolute inset-0 w-full h-full opacity-30 pointer-events-none" preserveAspectRatio="none" viewBox="0 0 100 100">
            {subsInGroup.map((_, i) => {
              const total = subsInGroup.length;
              const angle = (i / total) * Math.PI * 2 - Math.PI / 2;
              const x = 50 + Math.cos(angle) * 36;
              const y = 50 + Math.sin(angle) * 32;
              return <line key={i} x1="50" y1="50" x2={x} y2={y} stroke="#D4AF37" strokeWidth="0.15" />;
            })}
          </svg>

          {/* Center hub */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
            <div className="relative">
              <div className="absolute inset-0 bg-gold/30 blur-2xl rounded-full" />
              <div className="relative w-32 h-32 sm:w-36 sm:h-36 rounded-full bg-black border border-gold flex items-center justify-center">
                <div className="text-center">
                  <div className="font-display text-2xl text-white leading-none">220</div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-gold mt-1">Inc.</div>
                </div>
              </div>
            </div>
          </div>

          {/* Spokes */}
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="absolute inset-0"
            >
              {subsInGroup.map((s, i) => {
                const total = subsInGroup.length;
                const angle = (i / total) * Math.PI * 2 - Math.PI / 2;
                const x = 50 + Math.cos(angle) * 36;
                const y = 50 + Math.sin(angle) * 32;
                return (
                  <Link
                    key={s.slug}
                    to={s.href}
                    className="absolute -translate-x-1/2 -translate-y-1/2 group"
                    style={{ left: `${x}%`, top: `${y}%` }}
                    data-testid={`eco-spoke-${s.slug}`}
                  >
                    <div className="relative">
                      <div className="absolute -inset-2 bg-gold/0 group-hover:bg-gold/20 blur-md transition-all" />
                      <div className="relative border border-white/15 bg-[#121215] px-4 py-3 hover:border-gold transition-colors min-w-[140px] sm:min-w-[180px]">
                        <div className="font-display text-sm">{s.name}</div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-gold mt-1">{s.tag}</div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </Section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-white/5 pb-3">
      <span className="text-white/60 text-sm">{label}</span>
      <span className="font-display text-xl text-white">{value}</span>
    </div>
  );
}
